'use client';

import {
  type PresentingLocale,
  resolveTranslations,
  maskNamespacedMessages,
} from '@mentimeter/i18n-core';
import { usePrevious } from '@mentimeter/react-hooks';
import { NextIntlClientProvider, useLocale, useMessages } from 'next-intl';
import React, { useEffect, useState, type PropsWithChildren } from 'react';
import { useDevelopmentFlag } from '@mentimeter/development-tools/hooks';
import { getPresentingLocale } from '../getPresentingLocale';
import { messages as presentingMessages } from '../messages';

interface Props {
  language?: string;
}

export const NextIntlPresentingProvider = ({
  children,
  language,
}: PropsWithChildren<Props>) => {
  const presentingLocale = getPresentingLocale(language);
  const messages = useMessages();
  const maskMessagesOverride =
    useDevelopmentFlag('mask-translated-messages') === 'on';
  const locale = useLocale();

  const [resolvedPresentingMessages, setMessages] = useState(() =>
    resolveTranslations<PresentingLocale>(presentingLocale, presentingMessages),
  );

  const previousLocale = usePrevious<PresentingLocale, PresentingLocale>(
    presentingLocale,
    presentingLocale,
  );

  // Make sure resolveTranslations is only called when the locale changes
  useEffect(() => {
    if (maskMessagesOverride) {
      setMessages(
        resolveTranslations<PresentingLocale>(
          presentingLocale,
          maskNamespacedMessages(presentingMessages, { mask: '🛝 📊 🎾' }),
        ),
      );
      return;
    }

    if (previousLocale === presentingLocale) {
      return;
    } else {
      setMessages(
        resolveTranslations<PresentingLocale>(
          presentingLocale,
          presentingMessages,
        ),
      );
    }
  }, [presentingLocale, previousLocale, maskMessagesOverride]);

  return (
    <NextIntlClientProvider
      messages={{ ...messages, ...resolvedPresentingMessages }}
      locale={locale}
    >
      {children}
    </NextIntlClientProvider>
  );
};
